import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useAuth } from '@/auth/auth-context';
import { useCurrentMember } from '@/auth/member-context';
import { useGames } from '@/data/games';
import { queryKeys } from '@/data/keys';
import { useRealtimeInvalidation } from '@/data/useRealtimeInvalidation';
import { clearAllDrafts } from '@/comms/drafts';
import { UploadsPill } from '@/comms/UploadsPill';
import { useCommsNotifications } from '@/comms/useCommsNotifications';
import { commsBadge } from '@/comms/notify';
import { useChannelSettings, useUnread } from '@/data/comms';
import { GameSwitcher } from '@/grid/GameSwitcher';
import { desktopBridge } from '@/lib/bridge';
import { BUILD, useUpdateReady } from '@/lib/build';
import { useDeviceHeartbeat, useThisDeviceEndpoint } from '@/data/push';
import { appEnv } from '@/lib/env';
import { cn } from '@/lib/utils';
import { useStudioPresence } from '@/presence/useStudioPresence';
import { QuickSearch } from '@/scan/QuickSearch';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

export function AppShell() {
  const member = useCurrentMember();
  const { signOut, connectDrive } = useAuth();
  const { online, status } = useStudioPresence(member);
  // Profile edits reach every open window.
  useRealtimeInvalidation('members', queryKeys.members);
  useRealtimeInvalidation('games', queryKeys.games);
  const games = useGames();
  const updateReady = useUpdateReady();
  // While open here, this device rings its own notifications, so the push function skips it.
  useDeviceHeartbeat(useThisDeviceEndpoint().data);
  // Comms rings for new messages and keeps the sidebar's unread badge fresh.
  useCommsNotifications(member);
  useRealtimeInvalidation('channel_members', queryKeys.unread);
  const unread = useUnread();
  const channelSettings = useChannelSettings();
  const commsUnread = commsBadge(unread.data ?? [], channelSettings.data ?? []);

  // On a phone the sidebar is a slide-in menu. It closes itself on navigating,
  // because it's open only for the page it was opened on.
  const { pathname } = useLocation();
  const [menuAt, setMenuAt] = useState<string | null>(null);
  const menuOpen = menuAt === pathname;
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setMenuAt(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="scanlines grid h-full grid-cols-[260px_minmax(0,1fr)] max-[860px]:grid-cols-[minmax(0,1fr)]">
      <Sidebar
        version={desktopBridge()?.version}
        environment={appEnv.ok ? appEnv.env.VITE_HIVE_ENV : undefined}
        build={BUILD}
        badges={{ comms: commsUnread }}
        className={cn(
          'max-[860px]:hidden',
          menuOpen &&
            'max-[860px]:fixed max-[860px]:inset-y-0 max-[860px]:left-0 max-[860px]:z-50 max-[860px]:flex max-[860px]:w-[min(300px,85vw)]',
        )}
      >
        <GameSwitcher games={games.data ?? []} />
      </Sidebar>
      {menuOpen ? (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 bg-black/60 min-[861px]:hidden"
          onClick={() => setMenuAt(null)}
        />
      ) : null}
      <div className="bg-hud-grid flex min-w-0 flex-col overflow-hidden">
        <TopBar
          onMenu={() => setMenuAt(pathname)}
          member={member}
          online={online}
          presenceStatus={status}
          onSignOut={() => {
            // Drafts belong to whoever wrote them on this device.
            clearAllDrafts();
            void signOut();
          }}
          onConnectDrive={() => void connectDrive()}
          updateReady={updateReady}
        />
        <main className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <QuickSearch />
      <UploadsPill />
    </div>
  );
}
